<script setup lang="ts">
import type { TemperatureUnit } from "@/stores/settings.store";
import { computed } from "vue";
import { Droplets, Wind, Gauge, CloudOff, MapPin } from "@lucide/vue";
import { weatherDescription, type MainWeatherInfo } from "rainowls-shared";
import WeatherSourceInfo from "./WeatherSourceInfo.vue";
import CardErrorState from "@/components/ui/CardErrorState.vue";
import WeatherIconComplex from "./WeatherIconComplex.vue";
import RainfallPanel from "./RainfallPanel.vue";
import ConditionValueBar from "./ConditionValueBar.vue";
import CurrentConditionMetric from "./CurrentConditionMetric.vue";
import { finiteMeasurement, measurementRange } from "@/utils/conditionBar";
import { useUnitConversion } from "@/composables/useUnitConversion";
import { sanitizeValue, sanitizeWindSpeed } from "@/utils/weatherSanitizer";
import { validRain } from "@/utils/precipitationDisplay";

const props = defineProps<{
  wallMode?: boolean;
  weather: MainWeatherInfo | null;
  unit: TemperatureUnit;
  loading?: boolean;
  error?: boolean;
}>();

const { convertTemperature, convertSpeed, labels } = useUnitConversion(
  () => props.unit,
);

const direction = computed(() => {
  const bearing = sanitizeValue(props.weather?.windDirection);
  return bearing === null
    ? ""
    : ["N", "NE", "E", "SE", "S", "SW", "W", "NW"][
        Math.round(bearing / 45) % 8
      ];
});

const wind = computed(() =>
  convertSpeed(sanitizeWindSpeed(props.weather?.windSpeed)),
);

const humidity = computed(() => sanitizeValue(props.weather?.humidity));
const pressure = computed(() => sanitizeValue(props.weather?.pressure));

const daily = computed(() =>
  props.weather?.dailyWeather?.days?.length ? props.weather.dailyWeather : null,
);

// Shared aggregation already sums today's local-calendar-day hourly rain.
// NWS fallback supplies a placeholder zero, not a quantitative forecast.
const forecastRain = computed(() =>
  daily.value && props.weather?.dataSource !== "nws_fallback"
    ? validRain(daily.value.todayRain)
    : null,
);

const stationTime = computed(() => {
  const time = props.weather?.currentObservationTimestamp
    ? props.weather.stationFormattedTime
    : null;
  return time && !time.includes("NaN") ? time : null;
});

// Consume the shared daily aggregates directly. These are presentation inputs,
// not a second implementation of forecast/extreme aggregation.
const metricBars = computed(() => {
  const w = props.weather;
  const forecast =
    daily.value && w?.dataSource !== "nws_fallback" ? daily.value : null;
  /** Read the observed daily minimum and maximum for a metric. */
  const observed = (
    key: "temperature" | "humidity" | "windSpeed" | "pressure",
  ) => measurementRange(w?.dailyMinimums?.[key], w?.dailyMaximums?.[key]);
  return {
    temperature: {
      observed: observed("temperature"),
      forecast: forecast
        ? measurementRange(
            forecast.todayMinTemperature,
            forecast.todayMaxTemperature,
          )
        : null,
    },
    humidity: {
      observed: observed("humidity"),
      forecast: forecast
        ? measurementRange(forecast.todayHumidityLow, forecast.todayHumidity)
        : null,
    },
    wind: {
      observed: observed("windSpeed"),
      forecast: forecast ? measurementRange(0, forecast.todayWindSpeed) : null,
    },
    pressure: { observed: observed("pressure"), forecast: null },
  };
});

/** Format an available temperature in the selected units. */
function temperature(value: number | undefined) {
  return convertTemperature(sanitizeValue(value));
}
</script>
<template>
  <section
    class="conditions-strip relative z-2 bg-app-conditions"
    :class="
      wallMode
        ? 'h-full min-h-0 px-[18px] pt-[22px] pb-2 short-wall:px-3.5 short-wall:pt-5 short-wall:pb-1.5 compact-wall:pt-2 compact-wall:pb-2 tall-wall:px-6 tall-wall:pt-[30px] tall-wall:pb-5'
        : 'px-7 py-3.5 max-[600px]:px-5 max-[600px]:pt-6 min-[1200px]:[--current-icon-size:120px]'
    "
    :aria-busy="loading"
    aria-label="Current conditions"
  >
    <div
      v-if="loading"
      class="flex min-h-[180px] items-center justify-center text-text-muted"
      role="status"
    >
      Loading current conditions…
    </div>
    <CardErrorState
      v-else-if="error || !weather"
      class="min-h-[180px]"
      :icon="error ? CloudOff : MapPin"
      :message="
        error ? 'Current conditions unavailable' : 'No current observations'
      "
      description="Current measurements aren’t available for this location right now. Try another location or refresh later."
    />
    <template v-else>
      <WeatherSourceInfo
        :source="
          weather.isRainOwlsStation
            ? 'RainOwls station'
            : weather.currentObservationSource === 'open-meteo'
              ? 'Weather model'
              : 'Station'
        "
        :station-id="
          weather.stationId && weather.stationId !== 'Invalid'
            ? weather.stationId
            : 'Unavailable'
        "
        :timestamp="
          stationTime
            ? (weather.currentObservationSource === 'open-meteo'
                ? 'Model data '
                : 'Observed ') + stationTime
            : 'Unavailable'
        "
      />
      <div
        class="grid items-center"
        :class="
          wallMode
            ? 'h-full grid-cols-1 grid-rows-[auto_auto_minmax(0,1fr)] gap-2 compact-wall:gap-0 tall-wall:gap-[18px]'
            : 'grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-6 max-[1200px]:grid-cols-2 max-[600px]:grid-cols-1 max-[600px]:gap-[26px]'
        "
      >
        <div
          class="flex min-w-0 flex-col justify-center"
          :class="wallMode ? 'gap-0' : 'gap-4'"
        >
          <div
            class="grid min-w-0 grid-cols-[auto_minmax(0,max-content)_auto] items-center gap-x-4"
            :class="
              wallMode
                ? 'justify-center pr-5 [--current-icon-size:64px] tall-wall:[--current-icon-size:88px]'
                : 'justify-start [--current-icon-size:88px] max-[400px]:grid-cols-[auto_minmax(0,1fr)]'
            "
          >
            <CloudOff
              v-if="weather.weatherCode < 0"
              aria-hidden="true"
              class="size-14 text-text-muted"
            />
            <WeatherIconComplex
              v-else
              :weather-code="weather.weatherCode"
              :wind-speed="sanitizeWindSpeed(weather.windSpeed)"
              :is-day="weather.isDay ?? true"
              :size="88"
              :style="{
                width: 'var(--current-icon-size)',
                height: 'var(--current-icon-size)',
              }"
            />
            <div class="min-w-0 max-w-[16ch]">
              <h2 class="sr-only">
                Current conditions for {{ weather.locationName }}
              </h2>
              <div
                class="font-bold leading-none tracking-[-0.055em] tabular-nums"
                :class="
                  wallMode
                    ? 'text-[72px] short-wall:text-[60px] compact-wall:text-[56px]'
                    : 'text-[clamp(64px,5vw,88px)]'
                "
              >
                {{ temperature(weather.temperature) ?? "—"
                }}<span v-if="temperature(weather.temperature) !== null"
                  >°</span
                >
              </div>
              <p
                class="mt-1 font-semibold leading-tight"
                :class="wallMode ? 'text-base tall-wall:text-xl' : 'text-lg'"
              >
                {{
                  weather.weatherCode < 0
                    ? "Sky conditions unavailable"
                    : weatherDescription(weather.weatherCode)
                }}
              </p>
            </div>
            <dl
              class="grid grid-cols-[auto_auto] items-baseline gap-x-2 gap-y-1 text-text-muted tabular-nums [&_dd]:text-right [&_dd]:font-medium [&_dd]:text-text-primary"
              :class="
                wallMode
                  ? 'text-xs tall-wall:text-sm'
                  : 'text-sm max-[400px]:col-start-2 max-[400px]:mt-2 max-[400px]:justify-start'
              "
            >
              <dt>Feels like</dt>
              <dd>{{ temperature(weather.apparentTemperature) ?? "—" }}°</dd>
              <template v-if="daily">
                <dt>High</dt>
                <dd>{{ temperature(daily.todayMaxTemperature) ?? "—" }}°</dd>
                <dt>Low</dt>
                <dd>{{ temperature(daily.todayMinTemperature) ?? "—" }}°</dd>
              </template>
            </dl>
          </div>
          <div v-if="!wallMode" class="min-w-0 pl-[104px] max-[400px]:px-0">
            <ConditionValueBar
              class="max-w-72"
              :unit-preference="unit"
              label="Temperature"
              kind="temperature"
              :current="finiteMeasurement(weather.temperature)"
              v-bind="metricBars.temperature"
              :scale="[0, 100]"
              unit="°F"
            />
          </div>
        </div>
        <div
          class="grid min-w-0"
          :class="
            wallMode
              ? 'row-start-3 grid-cols-2 content-center items-start gap-x-4 gap-y-1 pt-0 short-wall:gap-y-0.5 tall-wall:gap-5'
              : 'gap-[15px] max-[1200px]:pt-[18px] max-[600px]:gap-[18px] max-[600px]:pt-0'
          "
        >
          <CurrentConditionMetric
            label="Wind"
            :icon="Wind"
            color="var(--weather-wind-text)"
            :value="`${direction} ${wind ?? '—'}`"
            :value-unit="labels.speed"
            kind="wind"
            :current="sanitizeWindSpeed(weather.windSpeed)"
            v-bind="metricBars.wind"
            :scale="[0, 30]"
            bar-unit="mph"
            :unit-preference="unit"
            :wall-mode="wallMode"
            full-width
          />
          <CurrentConditionMetric
            label="Humidity"
            :detail="`Dew point ${temperature(weather.dewPoint) ?? '—'}°`"
            :icon="Droplets"
            color="var(--weather-humidity-text)"
            :value="humidity === null ? '—' : `${Math.round(humidity)}%`"
            kind="humidity"
            :current="finiteMeasurement(weather.humidity)"
            v-bind="metricBars.humidity"
            :scale="[0, 100]"
            bar-unit="%"
            :unit-preference="unit"
            :wall-mode="wallMode"
          />
          <CurrentConditionMetric
            label="Pressure"
            :icon="Gauge"
            color="var(--weather-pressure-text)"
            :value="pressure === null ? '—' : `${Math.round(pressure)}`"
            value-unit="mb"
            kind="pressure"
            :current="finiteMeasurement(weather.pressure)"
            v-bind="metricBars.pressure"
            :scale="[980, 1040]"
            bar-unit="mb"
            :unit-preference="unit"
            :wall-mode="wallMode"
          />
        </div>
        <RainfallPanel
          :wall-mode="wallMode"
          :unit="unit"
          class="min-w-0"
          :class="
            wallMode
              ? 'col-start-1 row-start-2 border-y border-[var(--conditions-divider)] py-1 [&_strong]:text-[28px] short-wall:[&_strong]:text-2xl compact-wall:[&_strong]:text-xl'
              : 'pt-[18px] max-[1200px]:col-span-full max-[1200px]:pt-1 max-[600px]:col-auto'
          "
          :rate="weather.rainRate"
          :hourly="weather.rain1hr"
          :today="weather.rainDay"
          :storm="weather.rainStorm"
          :last24="weather.rain24hr"
          :forecast="forecastRain"
        />
      </div>
    </template>
  </section>
</template>
